import { IStairConfig, IStepGeometry, IStringerGeometry, IVec3 } from '@stair-craft/common/util';

import { STRINGER_THICKNESS, STRINGER_WIDTH } from '../../constants';
import { RESIDENTIAL_NORMS } from '../../norms';
import { kosourTopLine, riserSeatOf } from '../shared-kosour-top-line/kosour-top-line';
import { tetivaBoard } from '../shared-tetiva-board/tetiva-board';
import { buildFlightSawtoothNegZ } from '../u-shape-build-flight-sawtooth-neg-z/build-flight-sawtooth-neg-z';
import { buildFlightSawtoothZ } from '../u-shape-build-flight-sawtooth-z/build-flight-sawtooth-z';

/**
 * Left/right stringers for flight 1 (before the turn). Extracted from `generateStringers` to keep its complexity low.
 */
function buildFlight1Stringers(
    flight1Steps: IStepGeometry[],
    sides: ('left' | 'right')[],
    config: IStairConfig,
    riserHeight: number,
    turnStartZ: number,
    kosourBottom: { rise: number; run: number } | undefined
): IStringerGeometry[] {
    const result: IStringerGeometry[] = [];
    const f1First: IStepGeometry = flight1Steps[0];
    const lastStep: IStepGeometry = flight1Steps[flight1Steps.length - 1];
    const f1EndY: number = lastStep.position.y + riserHeight;

    for (const side of sides) {
        const x: number = side === 'left' ? 0 : config.stairWidth;
        if (!kosourBottom) {
            result.push(
                tetivaBoard({
                    side,
                    riserHeight,
                    origin: { x, z: f1First.position.z },
                    direction: { x: 0, z: 1 },
                    firstTop: f1First.position.y,
                    treadDepth: f1First.treadDepth,
                    noseLength: config.noseLength,
                    from: -config.noseLength,
                    to: turnStartZ - f1First.position.z,
                    maxY: config.floorHeight + config.floorThickness,
                })
            );
            continue;
        }
        const sawtooth: IVec3[] = buildFlightSawtoothZ(flight1Steps, x, 0, f1EndY, turnStartZ, 1);
        const points: IVec3[] = kosourTopLine(sawtooth, config.treadThickness, false, riserSeatOf(config));
        result.push({ side, points, thickness: STRINGER_THICKNESS, width: STRINGER_WIDTH, bottomSlope: kosourBottom });
    }

    return result;
}

/**
 * Per-side world X for a flight-2 stringer. Extracted from `generateStringers` to keep its complexity low.
 */
function computeFlight2X(side: 'left' | 'right', isRight: boolean, baseX: number, sw: number): number {
    if (isRight) {
        return side === 'left' ? baseX + sw : baseX;
    }
    return side === 'left' ? baseX : baseX + sw;
}

/**
 * Left/right stringers for flight 2 (after the turn). Extracted from `generateStringers` to keep its complexity low.
 */
function buildFlight2Stringers(
    flight2Steps: IStepGeometry[],
    sides: ('left' | 'right')[],
    config: IStairConfig,
    f2StartZ: number,
    riserHeight: number,
    kosourBottom: { rise: number; run: number } | undefined
): IStringerGeometry[] {
    const result: IStringerGeometry[] = [];
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    const maxY: number = config.floorHeight + config.floorThickness;
    const f2First: IStepGeometry = flight2Steps[0];
    const f2Last: IStepGeometry = flight2Steps[flight2Steps.length - 1];
    const f2StartY: number = f2First.position.y;
    const f2EndY: number = Math.min(f2Last.position.y + riserHeight, maxY);
    const f2TreadDepth: number = f2First.treadDepth;
    const f2EndZ: number = f2StartZ - flight2Steps.length * f2TreadDepth;
    const f2EntryY: number = Math.max(0, f2StartY - riserHeight);

    const baseX: number = isRight ? sw + config.flightGap : -(config.flightGap + sw);
    for (const side of sides) {
        const x: number = computeFlight2X(side, isRight, baseX, sw);
        // The board body lies on the stair side of its edge: the low-X edge is 'left' (+X inward).
        const inwardSide: 'left' | 'right' = x === baseX ? 'left' : 'right';
        if (!kosourBottom) {
            result.push(
                tetivaBoard({
                    riserHeight,
                    maxY,
                    side: inwardSide,
                    origin: { x, z: f2StartZ },
                    direction: { x: 0, z: -1 },
                    firstTop: f2StartY,
                    treadDepth: f2TreadDepth,
                    noseLength: f2First.noseLength,
                    from: 0,
                    to: f2StartZ - f2EndZ,
                })
            );
            continue;
        }
        // Flight 2 follows the turn; entry riser is owned by the previous segment.
        const sawtooth: IVec3[] = buildFlightSawtoothNegZ(
            flight2Steps.length,
            f2TreadDepth,
            x,
            f2StartZ,
            f2EntryY,
            f2EndY,
            riserHeight
        ).slice(1);
        const points: IVec3[] = kosourTopLine(sawtooth, config.treadThickness, true, riserSeatOf(config));
        result.push({ points, side: inwardSide, thickness: STRINGER_THICKNESS, width: STRINGER_WIDTH, bottomSlope: kosourBottom });
    }

    return result;
}

export function generateStringers(
    flight1Steps: IStepGeometry[],
    flight2Steps: IStepGeometry[],
    config: IStairConfig,
    riserHeight: number,
    turnStartZ: number,
    f2StartZ: number
): IStringerGeometry[] {
    const stringers: IStringerGeometry[] = [];
    const isKosour: boolean = config.stringerType === 'kosour';
    const kosourBottom: { rise: number; run: number } | undefined = isKosour
        ? { rise: riserHeight, run: RESIDENTIAL_NORMS.treadDepthIdeal }
        : undefined;

    const sides: ('left' | 'right')[] = ['left', 'right'];

    if (flight1Steps.length > 0) {
        stringers.push(...buildFlight1Stringers(flight1Steps, sides, config, riserHeight, turnStartZ, kosourBottom));
    }

    if (flight2Steps.length > 0) {
        stringers.push(...buildFlight2Stringers(flight2Steps, sides, config, f2StartZ, riserHeight, kosourBottom));
    }

    return stringers;
}
